/**
 * web/src/pages/HelpStaff.tsx
 * ヘルプスタッフ（単発勤務者）管理ページ（管理者専用）
 *
 * タブ:
 *   1. スタッフ管理 — help_staff のCRUD（通常アシスタントとは完全に独立したマスタ）
 *   2. 勤務管理     — one_time_work_entries のCRUD・提出リンク発行/無効化・
 *                     確認済み化・支払済み化。キャンセルは物理削除ではなくstatus遷移。
 */

import { useEffect, useState, useCallback } from 'react'
import { api } from '../api/client'
import type { HelpStaff as HelpStaffType, HelpWorkEntry, HelpAnnualSummary } from '../api/client'
import { ConfirmDialog, Toast } from '../components/ConfirmDialog'
import { WorkDetailsEditor } from '../components/WorkDetailsEditor'
import { emptyDetail, parseWorkDetails, isKaikaiKiki, parseImageUrls, normalizeImageUrl, type WorkDetail } from '../components/reportUtils'

function fmt(n: number | null | undefined) { return (n ?? 0).toLocaleString('ja-JP') }
function thisMonthJst() { return new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 7) }
function todayJst() { return new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10) }
function fmtDateTime(s: string | null | undefined) {
  if (!s) return '—'
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  return d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

const STATUS_LABELS: Record<string, string> = {
  pending: '未提出', draft: '一時保存', submitted: '提出済み', confirmed: '確認済み', cancelled: '無効化',
}
const STATUS_BADGE: Record<string, string> = {
  pending: 'badge-gray', draft: 'badge-amber', submitted: 'badge-blue', confirmed: 'badge-green', cancelled: 'badge-red',
}

type Tab = 'staff' | 'entries' | 'annual'

// ================================================================
// スタッフ編集モーダル
// ================================================================
function StaffEditModal({ initial, onClose, onSaved }: {
  initial: HelpStaffType | null
  onClose: () => void
  onSaved: () => void
}) {
  const isNew = !initial?.id
  const [form, setForm] = useState({
    name: initial?.name ?? '',
    default_unit_rate: initial?.default_unit_rate ?? 0,
    default_transport_fee: initial?.default_transport_fee ?? 0,
    default_overtime_rate: initial?.default_overtime_rate ?? 0,
    notes: initial?.notes ?? '',
    is_active: initial?.is_active ?? 1,
  })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const save = async () => {
    if (!form.name.trim()) { setErr('名前を入力してください'); return }
    setSaving(true); setErr(null)
    try {
      if (isNew) {
        await api.helpStaff.create(form)
      } else {
        await api.helpStaff.update(initial!.id, form)
      }
      onSaved()
      onClose()
    } catch (e: any) {
      setErr(e.message ?? '保存に失敗しました')
    } finally { setSaving(false) }
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <div className="modal-title">{isNew ? 'ヘルプスタッフを追加' : `${initial!.name} を編集`}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {err && <div style={{ color: 'var(--red)', fontSize: 12 }}>{err}</div>}
          <label style={{ fontSize: 12 }}>
            名前 <span style={{ color: 'var(--red)' }}>*</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              style={{ width: '100%', marginTop: 4 }} />
          </label>
          <label style={{ fontSize: 12 }}>
            デフォルト単価（円）
            <input type="number" min={0} value={form.default_unit_rate}
              onChange={(e) => setForm({ ...form, default_unit_rate: Number(e.target.value) })}
              style={{ width: '100%', marginTop: 4 }} />
          </label>
          <label style={{ fontSize: 12 }}>
            デフォルト交通費（円）
            <input type="number" min={0} value={form.default_transport_fee}
              onChange={(e) => setForm({ ...form, default_transport_fee: Number(e.target.value) })}
              style={{ width: '100%', marginTop: 4 }} />
          </label>
          <label style={{ fontSize: 12 }}>
            デフォルト残業時給（円）
            <input type="number" min={0} value={form.default_overtime_rate}
              onChange={(e) => setForm({ ...form, default_overtime_rate: Number(e.target.value) })}
              style={{ width: '100%', marginTop: 4 }} />
          </label>
          <label style={{ fontSize: 12 }}>
            メモ
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2} style={{ width: '100%', marginTop: 4 }} />
          </label>
          {!isNew && (
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={!!form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })} />
              有効（オフにするとスタッフ選択候補から外れます。過去データは残ります）
            </label>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>キャンセル</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ================================================================
// 勤務エントリ編集モーダル（新規作成 / 編集共通）
// ================================================================
function EntryEditModal({ initial, staffList, works, defaultMonth, onClose, onSaved }: {
  initial: HelpWorkEntry | null
  staffList: HelpStaffType[]
  works: { id: string; title: string }[]
  defaultMonth: string
  onClose: () => void
  onSaved: () => void
}) {
  const isNew = !initial?.id
  const [form, setForm] = useState({
    help_staff_id: initial?.help_staff_id ?? (staffList[0]?.id ?? ''),
    work_date: initial?.work_date ?? todayJst(),
    payment_target_month: initial?.payment_target_month ?? defaultMonth,
    unit_rate: initial?.unit_rate ?? (staffList[0]?.default_unit_rate ?? 0),
    transport_fee: initial?.transport_fee ?? (staffList[0]?.default_transport_fee ?? 0),
    notes: initial?.notes ?? '',
    report_issues: initial?.report_issues ?? '',
    // 勤務情報
    start_time: initial?.start_time ?? '',
    end_time: initial?.end_time ?? '',
    break_minutes: initial?.break_minutes ?? 0,
    work_note: initial?.work_note ?? '',
    // 残業（確定値。時給は staffList[0].default_overtime_rate から初期化）
    overtime_minutes: initial?.overtime_minutes ?? 0,
    overtime_rate: initial?.overtime_rate ?? (staffList[0]?.default_overtime_rate ?? 0),
    // その他経費（確定値）
    other_expense: initial?.other_expense ?? 0,
    expense_note: initial?.expense_note ?? '',
  })
  const [details, setDetails] = useState<WorkDetail[]>(
    () => parseWorkDetails(initial?.work_details) ?? [emptyDetail()]
  )
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [detailsError, setDetailsError] = useState<string | undefined>(undefined)
  const [needsConfirm, setNeedsConfirm] = useState(false)

  const applyDefaults = (staffId: string) => {
    const s = staffList.find((x) => x.id === staffId)
    setForm((f) => ({
      ...f, help_staff_id: staffId,
      unit_rate: s?.default_unit_rate ?? f.unit_rate,
      transport_fee: s?.default_transport_fee ?? f.transport_fee,
      overtime_rate: s?.default_overtime_rate ?? f.overtime_rate,
    }))
  }

  const doSave = async () => {
    const cleanDetails = details.filter((d) =>
      isKaikaiKiki(d.work_id, d.work_title)
        ? (d.memo ?? '').trim().length > 0
        : (d.work_title ?? '').trim().length > 0
    )
    if (cleanDetails.length === 0) {
      setDetailsError('少なくとも1件の作業内容を入力してください')
      return
    }
    setDetailsError(undefined)
    setSaving(true); setErr(null)
    try {
      const payload = { ...form, work_details: cleanDetails }
      if (isNew) {
        await api.helpWorkEntries.create(payload)
      } else {
        await api.helpWorkEntries.update(initial!.id, payload)
      }
      onSaved()
      onClose()
    } catch (e: any) {
      setErr(e.message ?? '保存に失敗しました')
    } finally { setSaving(false) }
  }

  const handleSaveClick = () => {
    if (!isNew && (initial!.status === 'submitted' || initial!.status === 'confirmed')) {
      setNeedsConfirm(true)
      return
    }
    doSave()
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <div className="modal-title">{isNew ? 'ヘルプ勤務を作成' : 'ヘルプ勤務を編集'}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '70vh', overflowY: 'auto' }}>
          {err && <div style={{ color: 'var(--red)', fontSize: 12 }}>{err}</div>}
          {!isNew && initial && (
            <div style={{ fontSize: 12, color: 'var(--ink-3)', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div>
                現在の状態: <span className={`badge ${STATUS_BADGE[initial.status]}`}>{STATUS_LABELS[initial.status]}</span>
              </div>
              <div>一時保存日時: {fmtDateTime(initial.draft_saved_at)}</div>
              <div>最終提出日時: {fmtDateTime(initial.submitted_at)}</div>
            </div>
          )}
          {isNew ? (
            <label style={{ fontSize: 12 }}>
              ヘルプスタッフ
              <select value={form.help_staff_id} onChange={(e) => applyDefaults(e.target.value)}
                style={{ width: '100%', marginTop: 4 }}>
                {staffList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
          ) : (
            <div style={{ fontSize: 12 }}>ヘルプスタッフ: <strong>{initial?.help_staff_name}</strong></div>
          )}

          {/* ── 基本情報 ── */}
          <div style={{ display: 'flex', gap: 12 }}>
            <label style={{ fontSize: 12, flex: 1 }}>
              勤務日
              <input type="date" value={form.work_date}
                onChange={(e) => setForm({ ...form, work_date: e.target.value })}
                style={{ width: '100%', marginTop: 4 }} />
            </label>
            <label style={{ fontSize: 12, flex: 1 }}>
              支払対象月
              <input type="month" value={form.payment_target_month}
                onChange={(e) => setForm({ ...form, payment_target_month: e.target.value })}
                style={{ width: '100%', marginTop: 4 }} />
            </label>
          </div>

          {/* ── 勤務情報 ── */}
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-2)', borderTop: '1px solid var(--paper-3)', paddingTop: 10 }}>
            勤務情報
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <label style={{ fontSize: 12, flex: 1 }}>
              開始時刻
              <input type="time" value={form.start_time ?? ''}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                style={{ width: '100%', marginTop: 4 }} />
            </label>
            <label style={{ fontSize: 12, flex: 1 }}>
              終了時刻
              <input type="time" value={form.end_time ?? ''}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                style={{ width: '100%', marginTop: 4 }} />
            </label>
            <label style={{ fontSize: 12, flex: 1 }}>
              休憩（分）
              <input type="number" min={0} value={form.break_minutes}
                onChange={(e) => setForm({ ...form, break_minutes: Number(e.target.value) })}
                style={{ width: '100%', marginTop: 4 }} />
            </label>
          </div>
          <label style={{ fontSize: 12 }}>
            勤務備考
            <textarea value={form.work_note ?? ''} onChange={(e) => setForm({ ...form, work_note: e.target.value })}
              rows={2} style={{ width: '100%', marginTop: 4 }} />
          </label>

          {/* ── 日報（work_details） ── */}
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-2)', borderTop: '1px solid var(--paper-3)', paddingTop: 10 }}>
            日報内容（本人提出内容。管理者が修正可）
          </div>
          <WorkDetailsEditor details={details} onChange={setDetails} works={works} errorMessage={detailsError} />
          <label style={{ fontSize: 12 }}>
            引継ぎ・連絡事項
            <textarea value={form.report_issues}
              onChange={(e) => setForm({ ...form, report_issues: e.target.value })}
              rows={2} style={{ width: '100%', marginTop: 4 }} />
          </label>

          {/* ── 金額（本人申告 / 管理者確定を並べて表示） ── */}
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-2)', borderTop: '1px solid var(--paper-3)', paddingTop: 10 }}>
            金額（本人申告 / 管理者確定）
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <label style={{ fontSize: 12, flex: 1 }}>
              単価（円）
              <input type="number" min={0} value={form.unit_rate}
                onChange={(e) => setForm({ ...form, unit_rate: Number(e.target.value) })}
                style={{ width: '100%', marginTop: 4 }} />
            </label>
          </div>

          {/* 交通費 */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
            <div style={{ fontSize: 12, flex: 1 }}>
              本人申告交通費
              <div style={{ marginTop: 4, padding: '7px 10px', background: 'var(--paper-2)', borderRadius: 6, color: 'var(--ink-3)' }}>
                {initial?.reported_transport_fee != null ? `¥${fmt(initial.reported_transport_fee)}` : '—'}
              </div>
            </div>
            <label style={{ fontSize: 12, flex: 1 }}>
              確定交通費（円）
              <input type="number" min={0} value={form.transport_fee}
                onChange={(e) => setForm({ ...form, transport_fee: Number(e.target.value) })}
                style={{ width: '100%', marginTop: 4 }} />
            </label>
            {!isNew && initial?.reported_transport_fee != null && (
              <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11, marginBottom: 2 }}
                onClick={() => setForm((f) => ({ ...f, transport_fee: initial.reported_transport_fee ?? f.transport_fee }))}>
                コピー
              </button>
            )}
          </div>

          {/* その他経費 */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
            <div style={{ fontSize: 12, flex: 1 }}>
              本人申告その他経費
              <div style={{ marginTop: 4, padding: '7px 10px', background: 'var(--paper-2)', borderRadius: 6, color: 'var(--ink-3)' }}>
                {initial?.reported_other_expense != null ? `¥${fmt(initial.reported_other_expense)}` : '—'}
              </div>
            </div>
            <label style={{ fontSize: 12, flex: 1 }}>
              確定その他経費（円）
              <input type="number" min={0} value={form.other_expense}
                onChange={(e) => setForm({ ...form, other_expense: Number(e.target.value) })}
                style={{ width: '100%', marginTop: 4 }} />
            </label>
            {!isNew && initial?.reported_other_expense != null && (
              <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11, marginBottom: 2 }}
                onClick={() => setForm((f) => ({ ...f, other_expense: initial.reported_other_expense ?? f.other_expense }))}>
                コピー
              </button>
            )}
          </div>
          <label style={{ fontSize: 12 }}>
            経費メモ（本人記入・管理者も編集可）
            <textarea value={form.expense_note ?? ''} onChange={(e) => setForm({ ...form, expense_note: e.target.value })}
              rows={2} style={{ width: '100%', marginTop: 4 }} />
          </label>

          {/* 残業 */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
            <div style={{ fontSize: 12, flex: 1 }}>
              本人申告残業時間（分・記録用）
              <div style={{ marginTop: 4, padding: '7px 10px', background: 'var(--paper-2)', borderRadius: 6, color: 'var(--ink-3)' }}>
                {initial?.reported_overtime_minutes ?? 0}分
              </div>
            </div>
            <label style={{ fontSize: 12, flex: 1 }}>
              確定残業時間（分）
              <input type="number" min={0} value={form.overtime_minutes}
                onChange={(e) => setForm({ ...form, overtime_minutes: Number(e.target.value) })}
                style={{ width: '100%', marginTop: 4 }} />
            </label>
            <label style={{ fontSize: 12, flex: 1 }}>
              残業時給（円）
              <input type="number" min={0} value={form.overtime_rate}
                onChange={(e) => setForm({ ...form, overtime_rate: Number(e.target.value) })}
                style={{ width: '100%', marginTop: 4 }} />
            </label>
            {!isNew && (
              <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11, marginBottom: 2 }}
                onClick={() => setForm((f) => ({ ...f, overtime_minutes: initial?.reported_overtime_minutes ?? f.overtime_minutes }))}>
                コピー
              </button>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            残業時給が0円の間は人件費に加算されません（記録のみの運用も可能）。
          </div>

          {/* ── 作業画像（本人提出。閲覧のみ） ── */}
          {!isNew && parseImageUrls(initial?.image_urls).length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-2)', borderTop: '1px solid var(--paper-3)', paddingTop: 10 }}>
                作業画像（本人提出）
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {parseImageUrls(initial?.image_urls).map(normalizeImageUrl).map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noreferrer">
                    <img src={url} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--paper-3)' }} />
                  </a>
                ))}
              </div>
            </>
          )}

          <label style={{ fontSize: 12 }}>
            備考（管理者用メモ）
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2} style={{ width: '100%', marginTop: 4 }} />
          </label>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSaveClick} disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>

      {needsConfirm && (
        <ConfirmDialog
          title="提出済みのヘルプ勤務を修正"
          message={`このヘルプ勤務は既に「${STATUS_LABELS[initial?.status ?? '']}」の状態です。\n内容を修正すると本人の提出内容が上書きされます。よろしいですか？`}
          confirmLabel="修正する"
          onConfirm={async () => { setNeedsConfirm(false); await doSave() }}
          onClose={() => setNeedsConfirm(false)}
        />
      )}
    </div>
  )
}

// ================================================================
// 無効化モーダル（理由入力・支払済み時は強い確認）
// ================================================================
function CancelEntryModal({ entry, onClose, onDone }: {
  entry: HelpWorkEntry
  onClose: () => void
  onDone: () => void
}) {
  const [reason, setReason] = useState('')
  const [ackPaid, setAckPaid] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const isPaid = !!entry.paid_at

  const submit = async () => {
    setSaving(true); setErr(null)
    try {
      await api.helpWorkEntries.cancel(entry.id, { reason: reason || undefined, confirm_paid: isPaid })
      onDone()
      onClose()
    } catch (e: any) {
      setErr(e.message ?? '無効化に失敗しました')
    } finally { setSaving(false) }
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <div className="modal-title">ヘルプ勤務を無効化</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.6 }}>
            {entry.help_staff_name} さん（{entry.work_date}）のヘルプ勤務を無効化します。データは削除されず「無効化」状態として残り、後から復帰できます。
          </div>
          {isPaid && (
            <div style={{
              background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.25)',
              borderRadius: 8, padding: '10px 14px',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--red)', marginBottom: 6 }}>
                ⚠ このエントリは支払済みです
              </div>
              <label style={{ fontSize: 12, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <input type="checkbox" checked={ackPaid} onChange={(e) => setAckPaid(e.target.checked)} style={{ marginTop: 2 }} />
                支払済みのデータであることを理解した上で無効化します
              </label>
            </div>
          )}
          <label style={{ fontSize: 12 }}>
            無効化理由（任意）
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              placeholder="例: 登録内容の誤り"
              style={{ width: '100%', marginTop: 4 }} />
          </label>
          {err && <div style={{ color: 'var(--red)', fontSize: 12 }}>{err}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>キャンセル</button>
          <button className="btn btn-danger" onClick={submit} disabled={saving || (isPaid && !ackPaid)}>
            {saving ? '処理中...' : '無効化する'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ================================================================
// 復帰モーダル（内容保持 / 内容リセットを選択）
// ================================================================
function RestoreEntryModal({ entry, onClose, onDone }: {
  entry: HelpWorkEntry
  onClose: () => void
  onDone: () => void
}) {
  const [saving, setSaving] = useState<'keep' | 'reset' | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const submit = async (resetContent: boolean) => {
    setSaving(resetContent ? 'reset' : 'keep')
    setErr(null)
    try {
      await api.helpWorkEntries.restore(entry.id, { reset_content: resetContent })
      onDone()
      onClose()
    } catch (e: any) {
      setErr(e.message ?? '復帰に失敗しました')
    } finally { setSaving(null) }
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <div className="modal-title">ヘルプ勤務を復帰</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.6 }}>
            {entry.help_staff_name} さん（{entry.work_date}）のヘルプ勤務を無効化前の状態に復帰します。
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            「内容を保持して復帰」は無効化前の提出内容をそのまま残します。「内容をリセットして復帰」は日報・勤怠・経費申告を空に戻したうえで未提出状態に戻します。
          </div>
          {err && <div style={{ color: 'var(--red)', fontSize: 12 }}>{err}</div>}
        </div>
        <div className="modal-footer" style={{ flexWrap: 'wrap', gap: 8 }}>
          <button className="btn btn-outline" onClick={onClose} disabled={!!saving}>キャンセル</button>
          <button className="btn btn-outline" onClick={() => submit(true)} disabled={!!saving}>
            {saving === 'reset' ? '処理中...' : '内容をリセットして復帰'}
          </button>
          <button className="btn btn-primary" onClick={() => submit(false)} disabled={!!saving}>
            {saving === 'keep' ? '処理中...' : '内容を保持して復帰'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ================================================================
// リンク発行モーダル
// ================================================================
function TokenModal({ entry, onClose }: { entry: HelpWorkEntry; onClose: () => void }) {
  const [issuing, setIssuing] = useState(false)
  const [result, setResult] = useState<{ raw_token: string; expires_at: string } | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const issue = async () => {
    setIssuing(true); setErr(null)
    try {
      const res = await api.helpWorkEntries.issueToken(entry.id)
      setResult(res)
    } catch (e: any) {
      setErr(e.message ?? 'リンクの発行に失敗しました')
    } finally { setIssuing(false) }
  }

  const url = result ? `${window.location.origin}/help-submit/${result.raw_token}` : ''
  const copy = async () => {
    if (!url) return
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000) }
    catch { /* clipboard 権限がない環境では手動選択を促す */ }
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <div className="modal-title">{entry.help_staff_name} さんの提出リンク発行</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {!result ? (
            <>
              <p style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.6 }}>
                新しいリンクを発行すると、既存の有効なリンクは自動的に無効になります。
              </p>
              {err && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 8 }}>{err}</div>}
              <button className="btn btn-primary btn-sm" onClick={issue} disabled={issuing}>
                {issuing ? '発行中...' : 'リンクを発行する'}
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 12, color: 'var(--amber)', marginBottom: 8, fontWeight: 600 }}>
                このURLはこの画面を閉じると再表示できません。今すぐコピーしてください。
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input readOnly value={url} onFocus={(e) => e.currentTarget.select()}
                  style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 12 }} />
                <button className="btn btn-outline btn-sm" onClick={copy}>{copied ? 'コピー済み' : 'コピー'}</button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8 }}>
                有効期限: {result.expires_at.slice(0, 10)}
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>閉じる</button>
        </div>
      </div>
    </div>
  )
}

// ================================================================
// メインページ
// ================================================================
export default function HelpStaffPage() {
  const [tab, setTab] = useState<Tab>('entries')
  const [staffList, setStaffList] = useState<HelpStaffType[]>([])
  const [staffStatusFilter, setStaffStatusFilter] = useState<'active' | 'all'>('active')
  const [entries, setEntries] = useState<HelpWorkEntry[]>([])
  const [works, setWorks] = useState<{ id: string; title: string }[]>([])
  const [month, setMonth] = useState(thisMonthJst())
  const [staffFilter, setStaffFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState<{ ok: boolean; msg: string } | null>(null)
  const [editStaff, setEditStaff] = useState<HelpStaffType | null | undefined>(undefined)
  const [editEntry, setEditEntry] = useState<HelpWorkEntry | null | undefined>(undefined)
  const [tokenEntry, setTokenEntry] = useState<HelpWorkEntry | null>(null)
  const [cancelEntry, setCancelEntry] = useState<HelpWorkEntry | null>(null)
  const [restoreEntry, setRestoreEntry] = useState<HelpWorkEntry | null>(null)
  const [resetConfirm, setResetConfirm] = useState<HelpWorkEntry | null>(null)

  // 年間支払集計タブ
  const [annualYear, setAnnualYear] = useState(() => new Date(Date.now() + 9 * 60 * 60 * 1000).getFullYear())
  const [annualFilter, setAnnualFilter] = useState<'paid' | 'confirmed'>('paid')
  const [annualData, setAnnualData] = useState<HelpAnnualSummary | null>(null)
  const [annualLoading, setAnnualLoading] = useState(false)

  const showToast = (ok: boolean, msg: string) => { setToast({ ok, msg }); setTimeout(() => setToast(null), 5000) }

  const loadStaff = useCallback(async () => {
    const list = await api.helpStaff.list({ status: staffStatusFilter })
    setStaffList(list)
  }, [staffStatusFilter])

  const loadEntries = useCallback(async () => {
    setLoading(true)
    try {
      const [list, allStaff, workList] = await Promise.all([
        api.helpWorkEntries.list({
          month: month || undefined,
          help_staff_id: staffFilter || undefined,
          status: statusFilter || undefined,
        }),
        api.helpStaff.list({ status: 'all' }),
        api.works.list().catch(() => []),
      ])
      setEntries(list)
      setStaffList(allStaff)
      setWorks(workList.map((w: any) => ({ id: w.id, title: w.title })))
    } finally { setLoading(false) }
  }, [month, staffFilter, statusFilter])

  const loadAnnual = useCallback(async () => {
    setAnnualLoading(true)
    try {
      const res = await api.helpWorkEntries.annualSummary(annualYear, annualFilter)
      setAnnualData(res)
    } finally { setAnnualLoading(false) }
  }, [annualYear, annualFilter])

  useEffect(() => { if (tab === 'staff') loadStaff() }, [tab, loadStaff])
  useEffect(() => { if (tab === 'entries') loadEntries() }, [tab, loadEntries])
  useEffect(() => { if (tab === 'annual') loadAnnual() }, [tab, loadAnnual])

  const handleReset = async (entry: HelpWorkEntry) => {
    try {
      await api.helpWorkEntries.reset(entry.id, { confirm_paid: !!entry.paid_at })
      showToast(true, '提出内容をリセットしました')
      loadEntries()
    } catch (e: any) { showToast(false, e.message) }
  }

  const handleConfirmEntry = async (entry: HelpWorkEntry) => {
    try {
      await api.helpWorkEntries.confirm(entry.id)
      showToast(true, '確認済みにしました')
      loadEntries()
    } catch (e: any) { showToast(false, e.message) }
  }

  const handleMarkPaid = async (entry: HelpWorkEntry) => {
    if (!window.confirm(`${entry.help_staff_name} さん（${entry.work_date}）を支払済みにします。よろしいですか？`)) return
    try {
      await api.helpWorkEntries.markPaid(entry.id)
      showToast(true, '支払済みにしました')
      loadEntries()
    } catch (e: any) { showToast(false, e.message) }
  }

  const handleRevokeToken = async (entry: HelpWorkEntry) => {
    try {
      await api.helpWorkEntries.revokeToken(entry.id)
      showToast(true, '提出リンクを無効化しました')
    } catch (e: any) { showToast(false, e.message) }
  }

  const activeTotal = entries.filter((e) => e.status !== 'cancelled')
    .reduce((s, e) => s + e.unit_rate + e.transport_fee, 0)

  return (
    <div>
      {toast && <Toast msg={toast.msg} ok={toast.ok} onClose={() => setToast(null)} />}

      {editStaff !== undefined && (
        <StaffEditModal initial={editStaff} onClose={() => setEditStaff(undefined)}
          onSaved={() => { loadStaff(); showToast(true, '保存しました') }} />
      )}

      {editEntry !== undefined && (
        <EntryEditModal initial={editEntry} staffList={staffList.filter((s) => s.is_active)}
          works={works}
          defaultMonth={month || thisMonthJst()}
          onClose={() => setEditEntry(undefined)}
          onSaved={() => { loadEntries(); showToast(true, '保存しました') }} />
      )}

      {tokenEntry && <TokenModal entry={tokenEntry} onClose={() => setTokenEntry(null)} />}

      {cancelEntry && (
        <CancelEntryModal entry={cancelEntry} onClose={() => setCancelEntry(null)}
          onDone={() => { loadEntries(); showToast(true, '無効化しました') }} />
      )}

      {restoreEntry && (
        <RestoreEntryModal entry={restoreEntry} onClose={() => setRestoreEntry(null)}
          onDone={() => { loadEntries(); showToast(true, '復帰しました') }} />
      )}

      {resetConfirm && (
        <ConfirmDialog
          title="提出内容をリセット"
          message={
            resetConfirm.paid_at
              ? `⚠ このエントリは支払済みです。\n${resetConfirm.help_staff_name} さん（${resetConfirm.work_date}）の日報・勤怠・経費申告の内容をすべて空に戻します。単価・確定額・備考などの管理項目は変更されません。\n本当にリセットしますか？`
              : `${resetConfirm.help_staff_name} さん（${resetConfirm.work_date}）の日報・勤怠・経費申告の内容をすべて空に戻します（勤務エントリ自体は残ります）。よろしいですか？`
          }
          confirmLabel="リセットする"
          confirmDanger
          onConfirm={async () => { await handleReset(resetConfirm); setResetConfirm(null) }}
          onClose={() => setResetConfirm(null)}
        />
      )}

      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>ヘルプスタッフ管理</h1>
      <p style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 16 }}>
        通常アシスタントとは別枠で、単発勤務のヘルプスタッフを管理します。
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button className={`btn btn-sm ${tab === 'entries' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setTab('entries')}>勤務管理</button>
        <button className={`btn btn-sm ${tab === 'staff' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setTab('staff')}>スタッフ管理</button>
        <button className={`btn btn-sm ${tab === 'annual' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setTab('annual')}>年間支払集計</button>
      </div>

      {/* ─────────────────────────────────────── */}
      {/* TAB: スタッフ管理                        */}
      {/* ─────────────────────────────────────── */}
      {tab === 'staff' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
            <button className="btn btn-outline btn-sm" onClick={() => setEditStaff(null)}>+ ヘルプスタッフを追加</button>
            <select value={staffStatusFilter} onChange={(e) => setStaffStatusFilter(e.target.value as 'active' | 'all')}
              style={{ fontSize: 12, marginLeft: 'auto' }}>
              <option value="active">有効のみ</option>
              <option value="all">アーカイブ含む</option>
            </select>
          </div>
          <div className="card" style={{ overflow: 'hidden' }}>
            <table style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>名前</th>
                  <th style={{ textAlign: 'right' }}>デフォルト単価</th>
                  <th style={{ textAlign: 'right' }}>デフォルト交通費</th>
                  <th>メモ</th>
                  <th>状態</th>
                  <th style={{ textAlign: 'right' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {staffList.map((s) => (
                  <tr key={s.id} style={{ opacity: s.is_active ? 1 : 0.5 }}>
                    <td style={{ fontWeight: 500 }}>{s.name}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(s.default_unit_rate)}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(s.default_transport_fee)}</td>
                    <td style={{ fontSize: 12, color: 'var(--ink-3)' }}>{s.notes ?? ''}</td>
                    <td>
                      <span className={`badge ${s.is_active ? 'badge-green' : 'badge-gray'}`}>
                        {s.is_active ? '有効' : 'アーカイブ'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn btn-sm btn-outline" style={{ fontSize: 11 }}
                        onClick={() => setEditStaff(s)}>編集</button>
                    </td>
                  </tr>
                ))}
                {staffList.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: 'var(--ink-3)' }}>
                    ヘルプスタッフが登録されていません。
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────── */}
      {/* TAB: 勤務管理                            */}
      {/* ─────────────────────────────────────── */}
      {tab === 'entries' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="btn btn-outline btn-sm" onClick={() => setEditEntry(null)}
              disabled={staffList.filter((s) => s.is_active).length === 0}>
              + ヘルプ勤務を作成
            </button>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              style={{ fontFamily: 'var(--font-mono)', fontSize: 13, width: 150 }} />
            <select value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)} style={{ fontSize: 12 }}>
              <option value="">全スタッフ</option>
              {staffList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ fontSize: 12 }}>
              <option value="">全ステータス</option>
              {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="loading" style={{ padding: 40 }}>LOADING...</div>
          ) : entries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-3)' }}>
              該当するヘルプ勤務がありません。
            </div>
          ) : (
            <div className="card" style={{ overflow: 'hidden' }}>
              <div className="table-wrap" style={{ overflowX: 'auto', width: '100%' }}>
                <table style={{ width: '100%', minWidth: 960 }}>
                  <thead>
                    <tr>
                      <th>勤務日</th>
                      <th>スタッフ名</th>
                      <th style={{ textAlign: 'right' }}>単価</th>
                      <th style={{ textAlign: 'right' }}>交通費(確定)</th>
                      <th style={{ textAlign: 'right' }}>交通費(申告)</th>
                      <th>提出状況</th>
                      <th>支払対象月</th>
                      <th>支払</th>
                      <th style={{ textAlign: 'right' }}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => (
                      <tr key={e.id} style={{ opacity: e.status === 'cancelled' ? 0.45 : 1 }}>
                        <td className="mono">{e.work_date}</td>
                        <td style={{ fontWeight: 500 }}>{e.help_staff_name}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(e.unit_rate)}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(e.transport_fee)}</td>
                        <td className="mono" style={{ textAlign: 'right', color: 'var(--ink-3)' }}>
                          {e.reported_transport_fee != null ? `¥${fmt(e.reported_transport_fee)}` : '—'}
                        </td>
                        <td>
                          <span className={`badge ${STATUS_BADGE[e.status]}`}
                            title={e.status === 'cancelled' && e.cancelled_reason ? `無効化理由: ${e.cancelled_reason}` : undefined}>
                            {STATUS_LABELS[e.status]}
                          </span>
                        </td>
                        <td className="mono">{e.payment_target_month}</td>
                        <td style={{ fontSize: 11, color: e.paid_at ? 'var(--green)' : 'var(--ink-3)' }}>
                          {e.paid_at ? '支払済' : '—'}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                          <button className="btn btn-sm btn-outline" style={{ fontSize: 11 }}
                            onClick={() => setEditEntry(e)}>編集</button>
                          {e.status !== 'cancelled' && !e.paid_at && (
                            <button className="btn btn-sm btn-outline" style={{ fontSize: 11, marginLeft: 4 }}
                              onClick={() => setTokenEntry(e)}>リンク発行</button>
                          )}
                          {e.status !== 'cancelled' && (
                            <button className="btn btn-sm btn-ghost" style={{ fontSize: 11, marginLeft: 4 }}
                              onClick={() => handleRevokeToken(e)}>リンク無効化</button>
                          )}
                          {e.status === 'submitted' && (
                            <button className="btn btn-sm btn-ghost" style={{ fontSize: 11, marginLeft: 4 }}
                              onClick={() => handleConfirmEntry(e)}>確認済みにする</button>
                          )}
                          {(e.status === 'submitted' || e.status === 'confirmed') && !e.paid_at && (
                            <button className="btn btn-sm btn-ghost" style={{ fontSize: 11, marginLeft: 4 }}
                              onClick={() => handleMarkPaid(e)}>支払済みにする</button>
                          )}
                          {e.status !== 'cancelled' && (
                            <button className="btn btn-sm btn-ghost" style={{ fontSize: 11, marginLeft: 4 }}
                              onClick={() => setResetConfirm(e)}>内容リセット</button>
                          )}
                          {e.status === 'cancelled' ? (
                            <button className="btn btn-sm btn-outline" style={{ fontSize: 11, marginLeft: 4 }}
                              onClick={() => setRestoreEntry(e)}>復帰</button>
                          ) : (
                            <button className="btn btn-sm btn-ghost" style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 4 }}
                              onClick={() => setCancelEntry(e)}>✕</button>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background: 'var(--paper-2)', fontWeight: 700 }}>
                      <td colSpan={2}>合計（無効化を除く{entries.filter((e) => e.status !== 'cancelled').length}件）</td>
                      <td colSpan={2} className="mono" style={{ textAlign: 'right' }}>¥{fmt(activeTotal)}</td>
                      <td colSpan={5}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────── */}
      {/* TAB: 年間支払集計                        */}
      {/* ─────────────────────────────────────── */}
      {tab === 'annual' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
            <select value={annualYear} onChange={(e) => setAnnualYear(Number(e.target.value))} style={{ fontSize: 12 }}>
              {Array.from({ length: 5 }, (_, i) => annualYear - 3 + i).map((y) => (
                <option key={y} value={y}>{y}年</option>
              ))}
            </select>
            <select value={annualFilter} onChange={(e) => setAnnualFilter(e.target.value as 'paid' | 'confirmed')} style={{ fontSize: 12 }}>
              <option value="paid">支払済みのみ</option>
              <option value="confirmed">確認済みを含む</option>
            </select>
          </div>

          {annualLoading ? (
            <div className="loading" style={{ padding: 40 }}>LOADING...</div>
          ) : !annualData || annualData.staff.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-3)' }}>
              {annualYear}年に勤務実績のあるヘルプスタッフがいません。
            </div>
          ) : (
            <div className="card" style={{ overflow: 'hidden' }}>
              <div className="table-wrap" style={{ overflowX: 'auto', width: '100%' }}>
                <table style={{ width: '100%', minWidth: 1100 }}>
                  <thead>
                    <tr>
                      <th>スタッフ名</th>
                      {Array.from({ length: 12 }, (_, i) => (
                        <th key={i} style={{ textAlign: 'right', fontSize: 11 }}>{i + 1}月</th>
                      ))}
                      <th style={{ textAlign: 'right' }}>合計</th>
                    </tr>
                  </thead>
                  <tbody>
                    {annualData.staff.map((s) => (
                      <tr key={s.help_staff_id}>
                        <td style={{ fontWeight: 500 }}>{s.name}</td>
                        {Array.from({ length: 12 }, (_, i) => {
                          const key = `${annualYear}-${String(i + 1).padStart(2, '0')}`
                          const amt = s.monthly[key] ?? 0
                          return (
                            <td key={i} className="mono" style={{ textAlign: 'right', fontSize: 11, color: amt ? 'var(--ink)' : 'var(--ink-3)' }}>
                              {amt ? fmt(amt) : '—'}
                            </td>
                          )
                        })}
                        <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>¥{fmt(s.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8 }}>
            「年間支払集計」は社内管理用の集計です。源泉徴収票・支払調書などの正式な税務書類ではありません。
          </div>
        </div>
      )}
    </div>
  )
}
